import { getContrastTextColor } from '../../utils/colorContrast'
import { GameType } from '../../constants/GameType'

export interface GameTypeBadge {
  label: string
  /** Badge fill. Hex, deliberately NOT from appColors (see below). */
  color: string
}

export interface ResolvedGameType extends GameTypeBadge {
  /**
   * Black or white, whichever reads on `color` — computed rather than assumed,
   * via the same `getContrastTextColor` the event category badge uses.
   *
   * Bright fills (the gold Final, the amber Championship) correctly take BLACK
   * text; white on those fails WCAG AA outright.
   */
  textColor: string
}

/**
 * Badge fills for the game types in `constants/GameType`.
 *
 * The same treatment the event category badge gets, for the same reason: a
 * game's type and an event's category answer the same question ("what kind of
 * thing is this?") and sit in the same slot on their respective rows, so they
 * must read as one system rather than two.
 *
 * Keyed by the type's NAME rather than its numeric id: the API sends
 * `game_type` as an object with a `name`, and the row already renders that
 * name. Matching on it keeps this list readable next to `GameType` without
 * threading ids through the view.
 *
 * The colours are literal hex rather than `appColors` tokens on purpose — the
 * same rule `EVENT_CATEGORIES` follows. They are a categorical palette that
 * must stay mutually distinguishable and identical on both tenants; pulling
 * them from the theme would make two types collide the moment a token changed.
 *
 * Grouped by competitive weight, and deliberately NOT reusing the event
 * palette's hues where the two could sit in one list: events lean blue/purple
 * for their on-field and assessment kinds, so the knockout rounds here take
 * the warm end.
 */
export const GAME_TYPE_COLORS: Record<string, string> = {
  // Season play — cool and quiet, the default weight
  [GameType[1]]: '#0891B2', // Exhibition
  [GameType[2]]: '#0369A1', // Regular Season
  [GameType[5]]: '#0E7490', // Preliminary
  [GameType[6]]: '#0284C7', // Round Robin
  // Post-season — warmer as the stakes rise
  [GameType[3]]: '#B45309', // Tournament
  [GameType[4]]: '#C2410C', // Playoff
  [GameType[7]]: '#9A3412', // Quarterfinal
  [GameType[8]]: '#9F1239', // Semifinal
  [GameType[13]]: '#BE185D', // Championship Semifinal
  // Finals — the brightest, and the only fills that take black text
  [GameType[9]]: '#CA8A04', // Final
  [GameType[11]]: '#EAB308', // Championship
  [GameType[15]]: '#A16207', // Bronze Final
  // Consolation — muted, deliberately reading as the lesser bracket
  [GameType[10]]: '#6D28D9', // Consolation Final
  [GameType[12]]: '#7C3AED', // Consolation
  [GameType[14]]: '#8B5CF6', // Consolation Semifinal
}

/** Fallback fill for a type the client does not know about. */
const UNKNOWN_GAME_TYPE_COLOR = '#64748B'

/**
 * The badge for a game's type, or null when it has none.
 *
 * An unrecognised type still renders — the backend owns this list and can add
 * to it, so a value we have no colour for is displayed with the neutral grey
 * rather than dropped. Matching is case-insensitive and whitespace-tolerant,
 * mirroring `getEventCategory`.
 */
export const getGameTypeBadge = (
  typeName: string | null | undefined
): ResolvedGameType | null => {
  const trimmed = typeName?.trim()
  if (!trimmed) {
    return null
  }

  const knownKey = Object.keys(GAME_TYPE_COLORS).find(
    (key) => key.toLowerCase() === trimmed.toLowerCase()
  )

  const color = knownKey ? GAME_TYPE_COLORS[knownKey] : UNKNOWN_GAME_TYPE_COLOR

  return { label: trimmed, color, textColor: getContrastTextColor(color) }
}
